import { useEffect, useRef } from "react";

import type { TRealtimeTicket } from "../services/realtime.service";

type TDatasetInputUpdate = {
	key: string;
	is_valid: boolean;
	error_msg?: string | null;
};

export type TDatasetInputUpdatedData = {
	step: string;
	updates: Array<TDatasetInputUpdate>;
};

export type TDatasetProcessingCompleteData = {
	step: string;
	status: "completed" | "error" | string;
	run_id: string;
};

export type TDatasetTapesLoadCompleteData = {
	step: string;
	status: "completed" | "error" | string;
	error_msg?: string | null;
};

type TRealtimeEnvelope<TData> = {
	id: string;
	type: string;
	channel: string | null;
	subject?: string | null;
	data: TData;
};

type TUseLedgerStepRealtimeOptions = {
	datasetId: string;
	step: string;
	onConnected?: () => void;
	onReadyToProcess?: () => void;
	onInputUpdated?: (data: TDatasetInputUpdatedData) => void;
	onProcessingComplete?: (
		data: TDatasetProcessingCompleteData
	) => void;
	onTapesLoadComplete?: (
		data: TDatasetTapesLoadCompleteData
	) => void;
	realtimeService: {
		createTicket: (
			channels: Array<string>
		) => Promise<TRealtimeTicket>;
		getStreamUrl: (token: string) => string;
	};
	enabled?: boolean;
};

const HEARTBEAT_WATCHDOG_MS = 45_000;
const MAX_RECONNECT_DELAY_MS = 10_000;

const parseEnvelope = <TData>(
	event: Event
): TRealtimeEnvelope<TData> | null => {
	try {
		return JSON.parse(
			(event as MessageEvent<string>).data
		) as TRealtimeEnvelope<TData>;
	} catch {
		return null;
	}
};

/** Keeps one guided ledger step reconciled with dataset SSE events. */
export const useLedgerStepRealtime = ({
	datasetId,
	step,
	onConnected,
	onReadyToProcess,
	onInputUpdated,
	onProcessingComplete,
	onTapesLoadComplete,
	realtimeService,
	enabled = true,
}: TUseLedgerStepRealtimeOptions): void => {
	const callbacksRef = useRef({
		onConnected,
		onReadyToProcess,
		onInputUpdated,
		onProcessingComplete,
		onTapesLoadComplete,
	});

	useEffect(() => {
		callbacksRef.current = {
			onConnected,
			onReadyToProcess,
			onInputUpdated,
			onProcessingComplete,
			onTapesLoadComplete,
		};
	});

	useEffect(() => {
		if (!enabled || !datasetId || !step) return;

		const channel = `dataset:${datasetId}`;
		const subject = `step:${step}`;
		const controller = new AbortController();
		let source: EventSource | null = null;
		let connecting = false;
		let reconnectAttempt = 0;
		let reconnectTimer: ReturnType<
			typeof setTimeout
		> | null = null;
		let watchdogTimer: ReturnType<
			typeof setTimeout
		> | null = null;
		const seenEventIds = new Set<string>();

		const clearTimer = (
			timer: ReturnType<typeof setTimeout> | null
		) => {
			if (timer) clearTimeout(timer);
		};

		const closeSource = () => {
			source?.close();
			source = null;
			clearTimer(watchdogTimer);
			watchdogTimer = null;
		};

		const matchesStep = <TData extends { step?: string }>(
			envelope: TRealtimeEnvelope<TData> | null
		) =>
			envelope?.channel === channel &&
			envelope.subject === subject &&
			envelope.data.step === step;

		const scheduleReconnect = () => {
			if (controller.signal.aborted || reconnectTimer)
				return;
			closeSource();
			reconnectAttempt += 1;
			reconnectTimer = setTimeout(
				() => {
					reconnectTimer = null;
					void connect();
				},
				Math.min(
					1_000 * 2 ** (reconnectAttempt - 1),
					MAX_RECONNECT_DELAY_MS
				)
			);
		};

		const resetWatchdog = () => {
			clearTimer(watchdogTimer);
			watchdogTimer = setTimeout(
				scheduleReconnect,
				HEARTBEAT_WATCHDOG_MS
			);
		};

		const handleStepEvent = <
			TData extends { step: string },
		>(
			event: Event,
			handler: (data: TData) => void
		) => {
			const envelope = parseEnvelope<TData>(event);
			if (!envelope || !matchesStep(envelope)) return;
			if (seenEventIds.has(envelope.id)) return;
			seenEventIds.add(envelope.id);
			resetWatchdog();
			handler(envelope.data);
		};

		const connect = async () => {
			if (controller.signal.aborted || connecting || source)
				return;
			connecting = true;
			try {
				const ticket = await realtimeService.createTicket([
					channel,
				]);
				// This effect can be cleaned up while the ticket request is pending.
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (controller.signal.aborted) return;
				if (
					!ticket.token ||
					!ticket.channels.includes(channel)
				) {
					scheduleReconnect();
					return;
				}

				source = new EventSource(
					realtimeService.getStreamUrl(ticket.token)
				);
				source.addEventListener("control.connected", () => {
					reconnectAttempt = 0;
					resetWatchdog();
					callbacksRef.current.onConnected?.();
				});
				source.addEventListener(
					"dataset.step.ready_to_process",
					(event) => {
						handleStepEvent<{ step: string }>(
							event,
							() => {
								callbacksRef.current.onReadyToProcess?.();
							}
						);
					}
				);
				source.addEventListener(
					"dataset.step.input_updated",
					(event) => {
						handleStepEvent<TDatasetInputUpdatedData>(
							event,
							(data) => {
								callbacksRef.current.onInputUpdated?.(data);
							}
						);
					}
				);
				source.addEventListener(
					"dataset.step.processing_complete",
					(event) => {
						handleStepEvent<TDatasetProcessingCompleteData>(
							event,
							(data) => {
								callbacksRef.current.onProcessingComplete?.(
									data
								);
							}
						);
					}
				);
				source.addEventListener(
					"dataset.tapes_load.complete",
					(event) => {
						handleStepEvent<TDatasetTapesLoadCompleteData>(
							event,
							(data) => {
								callbacksRef.current.onTapesLoadComplete?.(
									data
								);
							}
						);
					}
				);
				source.addEventListener(
					"control.heartbeat",
					resetWatchdog
				);
				source.addEventListener(
					"control.subscribed",
					resetWatchdog
				);
				source.onerror = scheduleReconnect;
				resetWatchdog();
			} catch {
				scheduleReconnect();
			} finally {
				connecting = false;
			}
		};

		void connect();

		return () => {
			controller.abort();
			closeSource();
			clearTimer(reconnectTimer);
		};
	}, [datasetId, enabled, realtimeService, step]);
};
